import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithCustomToken } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/lib/toast';
import { authenticateWithPasskey } from '@/lib/passkey-service';
import { useAuthStore } from '@/lib/stores/authStore';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { apiClient } from '@/lib/api';
import { signInWithGoogle } from '@/lib/auth-service';
import { AUTH_FORM_DEFAULTS } from '@/lib/constants';
import { useTheme } from '@/hooks/useTheme';
import { track, identifyUser } from '@/lib/analytics';
import AppStoreBadge from '@/components/common/AppStoreBadge';
import PlayStoreBadge from '@/components/common/PlayStoreBadge';

interface LocationState {
  from?: { pathname: string };
}

/** Firebase auth error shape */
interface FirebaseAuthError {
  code: string;
  message: string;
}

/** Convert Firebase auth error codes to user-friendly messages */
function getAuthErrorMessage(error: FirebaseAuthError): string {
  switch (error.code) {
    case 'auth/invalid-email':
      return 'Invalid email address format.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
      return 'Incorrect password.';
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/quota-exceeded':
      return 'Service temporarily unavailable. Please try again in a few minutes.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    default:
      return error.message || 'Failed to sign in. Please try again.';
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [email, setEmail] = useState(AUTH_FORM_DEFAULTS.email);
  const [password, setPassword] = useState(AUTH_FORM_DEFAULTS.password);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Reset state for clean login
      useLobbyStore.getState().reset();
      apiClient.clearAll();
      sessionStorage.removeItem('auto-login-quota-exceeded');

      identifyUser(cred.user.uid);
      track('login', { method: 'password' });

      const state = location.state as LocationState | null;
      const from = state?.from?.pathname || '/dashboard';

      // Flag for passkey offer — dashboard gates on user.passkeyRegistered
      // (hydrated by then) so this is safe to set unconditionally
      sessionStorage.setItem('offer-passkey-setup', 'true');

      toast.success('Signed in!');
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'code' in err
          ? getAuthErrorMessage(err as FirebaseAuthError)
          : 'Failed to sign in. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      // Popup resolves inline (setUser happens in signInWithGoogle); then route.
      const user = await signInWithGoogle();
      identifyUser(user.uid);
      track('login', { method: 'google' });
      const state = location.state as LocationState | null;
      const from = state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to sign in with Google. Please try again.'
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  const handlePasskeySignIn = async () => {
    if (!email) {
      setError('Enter your email first, then use passkey to sign in.');
      return;
    }

    setError(null);
    setPasskeyLoading(true);

    try {
      const customToken = await authenticateWithPasskey(email);
      const cred = await signInWithCustomToken(auth, customToken);
      useLobbyStore.getState().reset();
      apiClient.clearAll();
      useAuthStore.getState().setUser(cred.user);

      identifyUser(cred.user.uid);
      track('login', { method: 'passkey' });

      toast.success('Signed in with passkey!');

      const state = location.state as LocationState | null;
      const from = state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Passkey authentication failed:', err);
      const message = err instanceof Error ? err.message : 'Passkey authentication failed.';
      setError(`${message} Try email/password instead.`);
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <div className="text-center">
      <div className="flex flex-col items-center gap-3 mb-8">
        <img
          src={isDark ? '/polymyth_logo_v1_final_dark.png' : '/polymyth_logo_v1_final_lite.png'}
          alt="Polymyth"
          className="h-12 sm:h-16 w-auto"
        />
        <p className="text-xl sm:text-2xl font-character text-foreground">
          Roleplay Adventures for Everyone
        </p>
      </div>

      <h2 className="text-2xl font-bold text-foreground mb-2">Sign In</h2>
      <p className="text-muted-foreground mb-6">
        Don't have an account?{' '}
        <Link to="/signup" className="text-primary hover:underline">
          Sign up
        </Link>
      </p>

      <form onSubmit={handleEmailSignIn} className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive border border-destructive/30">
            {error}
          </div>
        )}

        <Input
          type="email"
          name="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Input
          type="password"
          name="password"
          autoComplete="current-password"
          enterKeyHint="go"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <Button
          type="submit"
          className="w-full uppercase tracking-wide bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
          disabled={loading || passkeyLoading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>

      {/* Passkey sign-in */}
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">or</span>
        </div>
      </div>

      <Button
        variant="outline"
        className="w-full uppercase tracking-wide"
        onClick={handlePasskeySignIn}
        disabled={loading || passkeyLoading || googleLoading}
      >
        {passkeyLoading ? 'Authenticating...' : 'Sign In with Passkey'}
      </Button>

      <Button
        variant="outline"
        className="w-full uppercase tracking-wide mt-3"
        onClick={handleGoogleSignIn}
        disabled={loading || passkeyLoading || googleLoading}
      >
        {googleLoading ? 'Connecting…' : 'Continue with Google'}
      </Button>

      <p className="mt-4 text-sm">
        <Link to="/reset-password" className="text-muted-foreground hover:text-foreground">
          Forgot your password?
        </Link>
      </p>

      {/* Mobile section */}
      <div
        className="mt-10 h-px w-full"
        style={{
          background:
            'linear-gradient(to right, transparent, hsl(var(--accent-gold) / 0.3), transparent)',
        }}
      />
      <div className="flex flex-col items-center gap-3 pt-8">
        <h3 className="text-xl sm:text-2xl font-character text-foreground">
          Play on phone or tablet
        </h3>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <AppStoreBadge />
          <PlayStoreBadge />
        </div>
      </div>
    </div>
  );
}
